'use client';

import { useState, useEffect } from 'react';
import SkillCategory from './SkillCategory';
import SkillFilter from './SkillFilter';
import SkillStats from './SkillStats';
import SkillSearch from './SkillSearch';
import SkillTimeline from './SkillTimeline';

/* =======================
   Types
======================= */

interface Skill {
  name: string;
  proficiency: number;
  yearsOfExperience: number;
  projects: string[];
  icon: string;
}

interface SkillCategoryData {
  id: string;
  title: string;
  skills: Skill[];
}

interface TimelineEvent {
  year: string;
  title: string;
  description: string;
  technologies: string[];
}

/* =======================
   Component
======================= */

export default function SkillsInteractive() {
  const [isHydrated, setIsHydrated] = useState(false);
  const [activeFilter, setActiveFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedCategories, setExpandedCategories] = useState<string[]>([
    'frontend'
  ]);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  /* =======================
     Skill Categories
  ======================= */

  const skillCategories: SkillCategoryData[] = [
  /* Frontend Development */
  {
    id: 'frontend',
    title: 'Frontend Development',
    skills: [
      {
        name: 'React.js',
        proficiency: 90,
        yearsOfExperience: 1,
        projects: [
          'Food Court Web Application',
          'SalesPilot Dashboard',
          'Personal Portfolio'
        ],
        icon: '⚛️'
      },
      {
        name: 'JavaScript (ES6+)',
        proficiency: 90,
        yearsOfExperience: 1,
        projects: [
          'Interactive Dashboards',
          'Frontend Business Logic',
          'Form Handling'
        ],
        icon: '🟨'
      },
      {
        name: 'HTML5 & CSS3',
        proficiency: 90,
        yearsOfExperience: 1,
        projects: [
          'Responsive Layouts',
          'UI Components',
          'Landing Pages'
        ],
        icon: '🌐'
      },
      {
        name: 'Tailwind CSS',
        proficiency: 85,
        yearsOfExperience: 1,
        projects: [
          'Design Systems',
          'Responsive UI',
          'Component Styling'
        ],
        icon: '🎨'
      }
    ]
  },

  /* Backend Development */
  {
    id: 'backend',
    title: 'Backend Development',
    skills: [
      {
        name: 'Python',
        proficiency: 85,
        yearsOfExperience: 1,
        projects: ['Flask APIs', 'Business Logic', 'Data Processing'],
        icon: '🐍'
      },
      {
        name: 'Flask',
        proficiency: 85,
        yearsOfExperience: 1,
        projects: [
          'RESTful APIs',
          'Authentication Systems',
          'CRUD Applications'
        ],
        icon: '🌶️'
      },
      {
        name: 'SQLAlchemy',
        proficiency: 80,
        yearsOfExperience: 1,
        projects: ['Relational Models', 'ORM Relationships', 'Database Migrations'],
        icon: '🗄️'
      },
      {
        name: 'REST APIs',
        proficiency: 85,
        yearsOfExperience: 1,
        projects: ['JWT Authentication', 'CRUD Endpoints', 'Role-Based Access'],
        icon: '🔗'
      }
    ]
  },

  /* Databases & Data */
  {
    id: 'database',
    title: 'Databases & Data',
    skills: [
      {
        name: 'PostgreSQL',
        proficiency: 80,
        yearsOfExperience: 1,
        projects: [
          'Food Court Database',
          'SalesPilot Data Models',
          'Relational Queries'
        ],
        icon: '🐘'
      },
      {
        name: 'SQLite',
        proficiency: 80,
        yearsOfExperience: 1,
        projects: ['Development Databases', 'Testing Environments'],
        icon: '📦'
      },
      {
        name: 'MongoDB',
        proficiency: 78,
        yearsOfExperience: 1,
        projects: ['NoSQL Solutions', 'Document Storage', 'Real-time Data'],
        icon: '🍃'
      }
    ]
  },

  /* Tools & DevOps */
  {
    id: 'tools',
    title: 'Tools & DevOps',
    skills: [
      {
        name: 'Git & GitHub',
        proficiency: 90,
        yearsOfExperience: 1,
        projects: ['Team Collaboration', 'Version Control', 'Pull Requests'],
        icon: '🔧'
      },
      {
        name: 'Docker',
        proficiency: 75,
        yearsOfExperience: 1,
        projects: ['Containerization', 'Development Environments', 'Deployment'],
        icon: '🐳'
      },
      {
        name: 'AWS',
        proficiency: 70,
        yearsOfExperience: 1,
        projects: ['Cloud Hosting', 'S3 Storage', 'Lambda Functions'],
        icon: '☁️'
      },
      {
        name: 'Vercel',
        proficiency: 85,
        yearsOfExperience: 1,
        projects: ['Next.js Deployment', 'Serverless Functions', 'Edge Computing'],
        icon: '▲'
      },
      {
        name: 'Postman',
        proficiency: 88,
        yearsOfExperience: 1,
        projects: ['API Testing', 'Request Automation', 'Environment Management'],
        icon: '📬'
      },
      {
        name: 'VS Code',
        proficiency: 92,
        yearsOfExperience: 1,
        projects: ['Development Setup', 'Extensions', 'Debugging'],
        icon: '💻'
      }
    ]
  },

  /* LLMs & AI */
  {
    id: 'ai',
    title: 'LLMs & AI Engineering',
    skills: [
      {
        name: 'Large Language Models (LLMs)',
        proficiency: 85,
        yearsOfExperience: 2,
        projects: ['AI Chatbots', 'Code Assistants', 'Knowledge-based Systems'],
        icon: '🤖'
      },
      {
        name: 'Prompt Engineering',
        proficiency: 88,
        yearsOfExperience: 2,
        projects: ['System Prompt Design', 'Few-shot Learning', 'Chain-of-Thought Prompting'],
        icon: '🧠'
      },
      {
        name: 'Retrieval Augmented Generation (RAG)',
        proficiency: 80,
        yearsOfExperience: 1,
        projects: ['Document Search', 'Vector Databases', 'AI Knowledge Assistants'],
        icon: '📚'
      },
      {
        name: 'LangChain',
        proficiency: 78,
        yearsOfExperience: 1,
        projects: ['Agent Workflows', 'Tool Calling', 'LLM Pipelines'],
        icon: '🔗'
      },
      {
        name: 'OpenAI API',
        proficiency: 82,
        yearsOfExperience: 2,
        projects: ['Chat Completions', 'AI Automation', 'LLM-powered Apps'],
        icon: '✨'
      }
    ]
  },

  /* Soft Skills & Methodologies */
  {
    id: 'soft',
    title: 'Soft Skills & Methodologies',
    skills: [
      {
        name: 'Communication',
        proficiency: 92,
        yearsOfExperience: 5,
        projects: ['Client Discussions', 'Technical Presentations', 'Team Syncs'],
        icon: '💬'
      },
      {
        name: 'Problem Solving',
        proficiency: 90,
        yearsOfExperience: 5,
        projects: ['Debugging', 'System Design', 'Performance Optimization'],
        icon: '🧩'
      },
      {
        name: 'Team Collaboration',
        proficiency: 89,
        yearsOfExperience: 5,
        projects: ['Cross-functional Teams', 'Pair Programming', 'Mentorship'],
        icon: '🤝'
      },
      {
        name: 'Agile / Scrum',
        proficiency: 85,
        yearsOfExperience: 4,
        projects: ['Sprint Planning', 'Standups', 'Retrospectives'],
        icon: '🔄'
      },
      {
        name: 'Leadership',
        proficiency: 83,
        yearsOfExperience: 3,
        projects: ['Project Ownership', 'Junior Dev Guidance', 'Decision Making'],
        icon: '🧭'
      }
    ]
  }
];

  /* =======================
     Learning Timeline
  ======================= */

  const timelineEvents: TimelineEvent[] = [
    {
      year: '2026',
      title: 'Full-Stack Project Development',
      description:
        'Built multiple production-ready full-stack applications using React and Flask, focusing on authentication, REST APIs, and relational databases.',
      technologies: ['React', 'Flask', 'PostgreSQL', 'SQLAlchemy', 'JWT']
    },
    {
      year: '2025',
      title: 'Software Engineering Training',
      description:
        'Completed intensive full-stack software engineering training at Moringa School with hands-on projects and team collaboration.',
      technologies: ['JavaScript', 'Python', 'React', 'Flask', 'Git']
    }
  ];

  /* =======================
     Helpers
  ======================= */

  const toggleCategory = (categoryId: string) => {
    if (!isHydrated) return;

    setExpandedCategories(prev =>
      prev.includes(categoryId)
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  const filteredCategories = skillCategories
    .filter(category =>
      activeFilter === 'all' ? true : category.id === activeFilter
    )
    .map(category => ({
      ...category,
      skills: category.skills.filter(
        skill =>
          searchQuery === '' ||
          skill.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          skill.projects.some(project =>
            project.toLowerCase().includes(searchQuery.toLowerCase())
          )
      )
    }))
    .filter(category => category.skills.length > 0);

  /* =======================
     Loading State
  ======================= */

  if (!isHydrated) {
    return (
      <div data-component-id="app/skills/components/SkillsInteractive.tsx:376:6" data-component-path="app/skills/components/SkillsInteractive.tsx" data-component-line="376" data-component-file="SkillsInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-h-screen%20bg-background%20flex%20items-center%20justify-center%22%7D" className="min-h-screen bg-background flex items-center justify-center">
        <p data-component-id="app/skills/components/SkillsInteractive.tsx:377:8" data-component-path="app/skills/components/SkillsInteractive.tsx" data-component-line="377" data-component-file="SkillsInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-muted-foreground%22%2C%22textContent%22%3A%22Loading%20skills...%22%7D" className="text-muted-foreground">Loading skills...</p>
      </div>
    );
  }

  /* =======================
     Render
  ======================= */

  return (
    <div data-component-id="app/skills/components/SkillsInteractive.tsx:387:4" data-component-path="app/skills/components/SkillsInteractive.tsx" data-component-line="387" data-component-file="SkillsInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22space-y-12%22%7D" className="space-y-12">
      <SkillStats data-component-id="app/skills/components/SkillsInteractive.tsx:388:6" data-component-path="app/skills/components/SkillsInteractive.tsx" data-component-line="388" data-component-file="SkillsInteractive.tsx" data-component-name="SkillStats" data-component-content="%7B%22elementName%22%3A%22SkillStats%22%7D" />

      <div data-component-id="app/skills/components/SkillsInteractive.tsx:390:6" data-component-path="app/skills/components/SkillsInteractive.tsx" data-component-line="390" data-component-file="SkillsInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22space-y-6%22%7D" className="space-y-6">
        <SkillSearch data-component-id="app/skills/components/SkillsInteractive.tsx:391:8" data-component-path="app/skills/components/SkillsInteractive.tsx" data-component-line="391" data-component-file="SkillsInteractive.tsx" data-component-name="SkillSearch" data-component-content="%7B%22elementName%22%3A%22SkillSearch%22%7D"
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
        />
        <SkillFilter data-component-id="app/skills/components/SkillsInteractive.tsx:395:8" data-component-path="app/skills/components/SkillsInteractive.tsx" data-component-line="395" data-component-file="SkillsInteractive.tsx" data-component-name="SkillFilter" data-component-content="%7B%22elementName%22%3A%22SkillFilter%22%7D"
          activeFilter={activeFilter}
          onFilterChange={setActiveFilter}
        />
      </div>

      <div data-component-id="app/skills/components/SkillsInteractive.tsx:401:6" data-component-path="app/skills/components/SkillsInteractive.tsx" data-component-line="401" data-component-file="SkillsInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22space-y-4%22%7D" className="space-y-4">
        {filteredCategories.length > 0 ? (
          filteredCategories.map(category => (
            <SkillCategory data-component-id="app/skills/components/SkillsInteractive.tsx:404:12" data-component-path="app/skills/components/SkillsInteractive.tsx" data-component-line="404" data-component-file="SkillsInteractive.tsx" data-component-name="SkillCategory" data-component-content="%7B%22elementName%22%3A%22SkillCategory%22%7D"
              key={category.id}
              title={category.title}
              skills={category.skills}
              isExpanded={expandedCategories.includes(category.id)}
              onToggle={() => toggleCategory(category.id)}
            />
          ))
        ) : (
          <div data-component-id="app/skills/components/SkillsInteractive.tsx:413:10" data-component-path="app/skills/components/SkillsInteractive.tsx" data-component-line="413" data-component-file="SkillsInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-center%20py-12%22%7D" className="text-center py-12">
            <p data-component-id="app/skills/components/SkillsInteractive.tsx:414:12" data-component-path="app/skills/components/SkillsInteractive.tsx" data-component-line="414" data-component-file="SkillsInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-text-secondary%20text-lg%22%2C%22textContent%22%3A%22No%20skills%20found%20matching%20your%20search.%22%7D" className="text-text-secondary text-lg">
              No skills found matching your search.
            </p>
          </div>
        )}
      </div>

      <div data-component-id="app/skills/components/SkillsInteractive.tsx:421:6" data-component-path="app/skills/components/SkillsInteractive.tsx" data-component-line="421" data-component-file="SkillsInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22bg-card%20border%20border-border%20rounded-lg%20p-8%20shadow-brand%22%7D" className="bg-card border border-border rounded-lg p-8 shadow-brand">
        <h2 data-component-id="app/skills/components/SkillsInteractive.tsx:422:8" data-component-path="app/skills/components/SkillsInteractive.tsx" data-component-line="422" data-component-file="SkillsInteractive.tsx" data-component-name="h2" data-component-content="%7B%22elementName%22%3A%22h2%22%2C%22className%22%3A%22text-2xl%20font-headline%20font-bold%20text-foreground%20mb-8%20text-center%22%2C%22textContent%22%3A%22Learning%20Journey%20Timeline%22%7D" className="text-2xl font-headline font-bold text-foreground mb-8 text-center">
          Learning Journey Timeline
        </h2>
        <SkillTimeline data-component-id="app/skills/components/SkillsInteractive.tsx:425:8" data-component-path="app/skills/components/SkillsInteractive.tsx" data-component-line="425" data-component-file="SkillsInteractive.tsx" data-component-name="SkillTimeline" data-component-content="%7B%22elementName%22%3A%22SkillTimeline%22%7D" events={timelineEvents} />
      </div>
    </div>
  );
}
